import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_SERVER } from '../config/env';
import { getAuthToken } from '../services/authSession';

const SOCKET_URL = typeof API_SERVER === 'string' ? API_SERVER.trim().replace(/\/+$/, '') : '';
export const BENEFITS_QUEUE_ROOM = 'benefits:queue';

/**
 * Conecta ao Socket.IO do server-eurobem, entra na sala da fila de cobrança
 * e chama onUpdate quando a fila benefits-club muda.
 * @param {{ onUpdate?: (payload: object) => void, enabled?: boolean }} [options]
 */
export function useBenefitsQueueSocket(options = {}) {
    const { onUpdate, enabled = true } = options;
    const [connected, setConnected] = useState(false);
    const onUpdateRef = useRef(onUpdate);
    const debounceRef = useRef(null);

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    const flushUpdate = useCallback(payload => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onUpdateRef.current?.(payload);
        }, 350);
    }, []);

    useEffect(() => {
        if (!enabled || !SOCKET_URL) {
            setConnected(false);
            return undefined;
        }

        const token = getAuthToken();
        if (!token) {
            setConnected(false);
            return undefined;
        }

        const socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 12,
            reconnectionDelay: 1500,
        });

        const onConnect = () => {
            setConnected(true);
            socket.emit('join:room', BENEFITS_QUEUE_ROOM);
        };

        const onDisconnect = () => setConnected(false);
        const onQueueUpdate = payload => flushUpdate(payload);

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('benefits:queue:update', onQueueUpdate);

        if (socket.connected) onConnect();

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            socket.emit('leave:room', BENEFITS_QUEUE_ROOM);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('benefits:queue:update', onQueueUpdate);
            socket.disconnect();
            setConnected(false);
        };
    }, [enabled, flushUpdate]);

    return { connected, room: BENEFITS_QUEUE_ROOM };
}

export default useBenefitsQueueSocket;
